import QueryKeys from '@src/constants/queryKeys';
import useStackMutations from '@src/hooks/api/mutations/useStackMutations';
import useControlModeTemplateMutations from '@src/hooks/api/mutations/useTemplateMutations';
import useGetControlModeTemplateById from '@src/hooks/api/queries/modes/useGetControlModeTemplateById';
import useGetStackById from '@src/hooks/api/queries/stacks/useGetStackById';
import {
  ControlModeTemplate,
  ControlModeTemplateField,
  ControlModeTemplateFieldsType,
  ControlModeTemplateSettings,
} from '@src/types/ControlModes';
import {
  ISections,
  Stack,
  ControlModes as StackControlModes,
} from '@src/types/Stacks';
import { useQueryClient } from '@tanstack/react-query';
import { UUID } from 'crypto';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useReducer,
} from 'react';
import {
  convertFieldsToStackFormat,
  convertStackToTemplateFormat,
} from '../helpers/fieldConverters';
import {
  prepareTemplateForApiSubmission,
  prepareUpdatedSettings,
} from '../helpers/fieldMappers';
import { validateFields } from '../helpers/fieldValidators';
import { formReducer, initialState } from './formReducer';
import { FormAction, FormActionType, FormState, FormValue } from './types';

// Create the context
interface FormContextType {
  state: FormState;
  dispatch: React.Dispatch<FormAction>;
}

export const FormContext = createContext<FormContextType | undefined>(
  undefined,
);

// Create the provider component
interface FormProviderProps {
  children: ReactNode;
  templateId?: string;
  type?: 'template' | 'stack';
  onClose?: () => void;
  onSave?: (saveFunction: () => Promise<void>, isSaving: boolean) => void;
}

export function FormProvider({
  children,
  templateId,
  type = 'template',
  onClose,
  onSave,
}: FormProviderProps) {
  const [state, dispatch] = useReducer(formReducer, initialState);
  const queryClient = useQueryClient();

  // API hooks
  const { data: templateData } = useGetControlModeTemplateById({
    id: templateId!,
    enabled: !!templateId && type === 'template',
  });

  const { data: stackData } = useGetStackById({
    id: templateId!,
    enabled: !!templateId && type === 'stack',
  });

  const { createControlModeTemplate, updateControlModeTemplate } =
    useControlModeTemplateMutations(templateId);

  const { createStack, updateStack } = useStackMutations(templateId);

  // Initialize form values when data is loaded
  useEffect(() => {
    if (templateId && (templateData || stackData)) {
      if (type === 'template' && templateData && templateData.settings) {
        // Map template fields to form values for existing template
        const newFormValues: Record<string, FormValue> = {};
        const newGroupedFields: FormState['groupedFields'] = [];

        templateData.settings.forEach(
          (setting: ControlModeTemplateSettings) => {
            // Create a group for each setting
            newGroupedFields.push({
              uiLabel: setting.uiLabel,
              controllerField: setting.controllerField,
              fields: setting.fields,
            });

            // Set form values for all fields
            setting.fields.forEach((field: ControlModeTemplateField) => {
              if (field.defaultValue !== undefined) {
                // Set initial form values based on the field's default value
                newFormValues[field.controllerField] = field.defaultValue;
              } else if (field.type === ControlModeTemplateFieldsType.BOOLEAN) {
                // For boolean fields, set a default value of false if not provided
                newFormValues[field.controllerField] = false;
              }
            });
          },
        );

        dispatch({
          type: FormActionType.SET_FORM_VALUES,
          payload: newFormValues,
        });
        dispatch({
          type: FormActionType.SET_ORIGINAL_VALUES,
          payload: newFormValues,
        });
        dispatch({
          type: FormActionType.SET_GROUPED_FIELDS,
          payload: newGroupedFields,
        });
        dispatch({
          type: FormActionType.SET_UNSAVED_SETTINGS,
          payload: templateData.settings,
        });
        dispatch({ type: FormActionType.SET_NAME, payload: templateData.name });
      } else if (type === 'stack' && stackData) {
        // Use the helper function to convert stack data to template format
        const { formValues, groupedFields, settings } =
          convertStackToTemplateFormat(stackData);

        dispatch({ type: FormActionType.SET_FORM_VALUES, payload: formValues });
        dispatch({
          type: FormActionType.SET_ORIGINAL_VALUES,
          payload: formValues,
        });
        dispatch({
          type: FormActionType.SET_GROUPED_FIELDS,
          payload: groupedFields,
        });
        dispatch({
          type: FormActionType.SET_UNSAVED_SETTINGS,
          payload: settings,
        });
        dispatch({
          type: FormActionType.SET_NAME,
          payload: stackData.name || '',
        });
      }
    } else if (!templateId) {
      // Initialize empty form for new template/stack
      dispatch({ type: FormActionType.SET_FORM_VALUES, payload: {} });
      dispatch({ type: FormActionType.SET_ORIGINAL_VALUES, payload: {} });
      dispatch({ type: FormActionType.SET_GROUPED_FIELDS, payload: [] });
      dispatch({ type: FormActionType.SET_UNSAVED_SETTINGS, payload: [] });
    }
  }, [templateData, stackData, templateId, type]);

  // Handle save operation
  const handleSave = useCallback(async () => {
    // Set that user has attempted to submit the form
    dispatch({ type: FormActionType.SET_HAS_ATTEMPTED_SUBMIT, payload: true });
    dispatch({ type: FormActionType.SET_IS_SAVING, payload: true });
    dispatch({ type: FormActionType.SET_FIELD_ERRORS, payload: {} });

    // Track if there are any validation errors
    let hasErrors = false;
    let errors: Record<string, string> = {};

    // Validate name
    if (!state.name.trim()) {
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: `${type === 'template' ? 'Template' : 'Stack'} name is required`,
          severity: 'error',
        },
      });
      hasErrors = true;
    }

    // Check if there are any settings
    if (!state.unsavedSettings || state.unsavedSettings.length === 0) {
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: 'At least one setting is required',
          severity: 'error',
        },
      });
      hasErrors = true;
    }
    // Validate all fields in all settings
    else {
      const validationResult = validateFields(
        state.unsavedSettings,
        state.formValues,
      );
      if (validationResult.hasErrors) {
        errors = validationResult.errors;
        hasErrors = true;
      }
    }

    // Update field errors state
    if (Object.keys(errors).length > 0) {
      dispatch({ type: FormActionType.SET_FIELD_ERRORS, payload: errors });
    }

    // If there are validation errors, show a message and stop
    if (hasErrors) {
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: 'Please correct the errors before saving',
          severity: 'error',
        },
      });
      dispatch({ type: FormActionType.SET_IS_SAVING, payload: false });
      return;
    }

    // Use unsavedSettings if available, otherwise use data?.settings or empty array
    const settingsToUpdate =
      state.unsavedSettings ||
      (type === 'template' ? templateData?.settings || [] : []);

    // Prepare updated settings with proper DTO types - include all fields with updated values
    const updatedSettings: ControlModeTemplateSettings[] =
      prepareUpdatedSettings(settingsToUpdate, state.formValues);

    try {
      if (type === 'template') {
        // Prepare template data for create/update
        const templateData: ControlModeTemplate = {
          id: templateId as UUID, // Include id for type safety, but it will be omitted in the API call
          name: state.name,
          settings: updatedSettings,
        };

        if (templateId) {
          // Update existing template
          // Omit the id property before sending to the API
          const templateDataForApi =
            prepareTemplateForApiSubmission(templateData);
          await updateControlModeTemplate.mutateAsync(templateDataForApi);
          // Invalidate templates list query to trigger refetch with same timeout as closing form
          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.controlModeTemplates],
            });
          }, 1000);
          dispatch({
            type: FormActionType.SHOW_SNACKBAR,
            payload: {
              message: 'Template updated successfully',
              severity: 'success',
            },
          });
        } else {
          // Create new template
          const templateDataForApi =
            prepareTemplateForApiSubmission(templateData);
          await createControlModeTemplate.mutateAsync(templateDataForApi);
          // Invalidate templates list query to trigger refetch with same timeout as closing form
          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.controlModeTemplates],
            });
          }, 1000);
          dispatch({
            type: FormActionType.SHOW_SNACKBAR,
            payload: {
              message: 'Template created successfully',
              severity: 'success',
            },
          });
        }

        // Update original values to match current values
        const newOriginalValues = { ...state.originalValues };
        if (templateData.settings) {
          templateData.settings.forEach(
            (setting: ControlModeTemplateSettings) => {
              setting.fields.forEach((field: ControlModeTemplateField) => {
                if (state.formValues[field.controllerField] !== undefined) {
                  newOriginalValues[field.controllerField] =
                    state.formValues[field.controllerField];
                }
              });
            },
          );
        }
        dispatch({
          type: FormActionType.SET_ORIGINAL_VALUES,
          payload: newOriginalValues,
        });
      } else if (type === 'stack') {
        // Create sections from updated settings
        const updatedSections = updatedSettings.map((setting) => {
          // Create a properly typed section
          const section: ISections = {
            controllerField: setting.controllerField,
            uiLabel: setting.uiLabel,
            fields: setting.fields.map(convertFieldsToStackFormat),
          };
          return section;
        });

        // Prepare stack data
        let baseStackData: Omit<Stack, 'id'>;

        if (templateId && stackData && stackData.controlModes) {
          // If updating an existing stack, preserve the control modes structure
          // but update the first control mode's sections with the new values
          const updatedControlModes = [...stackData.controlModes];

          if (updatedControlModes.length > 0) {
            // Update the first control mode's sections
            updatedControlModes[0] = {
              ...updatedControlModes[0],
              sections: updatedSections,
            };
          } else {
            // If no control modes exist, create a default one
            updatedControlModes.push({
              name: 'Default',
              enabled: true,
              sections: updatedSections,
            });
          }

          baseStackData = {
            name: state.name,
            controlModes: updatedControlModes,
          };
        } else {
          // For new stacks, create with the structure from the API example
          baseStackData = {
            name: state.name,
            controlModes: [
              {
                name: 'AGC Response',
                enabled: true,
                sections: updatedSections,
              } as StackControlModes,
            ],
          };
        }

        if (templateId) {
          // Update existing stack - add ID to the data
          const updateStackData: Stack = {
            ...baseStackData,
            id: templateId as UUID,
          };
          await updateStack.mutateAsync(updateStackData);
          // Invalidate templates list query to trigger refetch with same timeout as closing form
          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.controlModeTemplates],
            });
          }, 1000);
          dispatch({
            type: FormActionType.SHOW_SNACKBAR,
            payload: {
              message: 'Stack updated successfully',
              severity: 'success',
            },
          });
        } else {
          // Create new stack - no ID needed
          await createStack.mutateAsync(baseStackData);
          // Invalidate templates list query to trigger refetch with same timeout as closing form
          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: [QueryKeys.controlModeTemplates],
            });
          }, 1000);
          dispatch({
            type: FormActionType.SHOW_SNACKBAR,
            payload: {
              message: 'Stack created successfully',
              severity: 'success',
            },
          });
        }
      }

      // Close the modal on successfully create/edit with a timeout to avoid flicker
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 1000);
    } catch (error) {
      console.error(
        `Failed to ${templateId ? 'update' : 'create'} ${type}:`,
        error,
      );

      // Show error notification
      dispatch({
        type: FormActionType.SHOW_SNACKBAR,
        payload: {
          message: `Failed to ${templateId ? 'update' : 'create'} ${type}. Please check console for details.`,
          severity: 'error',
        },
      });
    } finally {
      // Reset saving state
      dispatch({ type: FormActionType.SET_IS_SAVING, payload: false });
      dispatch({ type: FormActionType.SET_EDITING_FIELD, payload: null });
      dispatch({ type: FormActionType.SET_IS_EDIT_MODE, payload: false });
    }
  }, [
    state,
    type,
    templateId,
    templateData,
    stackData,
    updateControlModeTemplate,
    createControlModeTemplate,
    updateStack,
    createStack,
    queryClient,
    onClose,
  ]);

  // Expose handleSave function to parent component
  // We use useRef to store the handleSave function to avoid infinite re-renders
  const handleSaveRef = React.useRef(handleSave);

  // Update the ref when handleSave changes
  React.useEffect(() => {
    handleSaveRef.current = handleSave;
  }, [handleSave]);

  // Only call onSave when onSave or isSaving changes, not when handleSave changes
  React.useEffect(() => {
    if (onSave) {
      // Use the ref's current value to avoid dependency on handleSave
      onSave(() => handleSaveRef.current(), state.isSaving);
    }
  }, [onSave, state.isSaving]);

  // Create a context value with state and dispatch
  const contextValue: FormContextType = {
    state,
    dispatch,
  };

  return (
    <FormContext.Provider value={contextValue}>{children}</FormContext.Provider>
  );
}

// Create a hook to use the context
export function useFormContext() {
  const context = useContext(FormContext);
  if (context === undefined) {
    throw new Error('useFormContext must be used within a FormProvider');
  }
  return context;
}
